import { api } from '@/lib/api'

/**
 * @typedef {object} MessagingSettings
 * @property {boolean} appointment_confirmation_enabled
 * @property {{ driver: string, configured: boolean, status_label: string }} whatsapp
 * @property {{ driver: string, fallback_enabled: boolean }} sms
 * @property {{ id: number | null, name: string, body: string }} confirmation_template
 * @property {{ id: number | null, name: string, body: string }} promotional_template
 * @property {{ all_with_phone: number, marketing_consent: number }} promotional_recipient_counts
 */

/**
 * @returns {Promise<MessagingSettings>}
 */
export async function fetchMessagingSettings() {
  const { data } = await api.get('/masters/messaging-settings')
  return data.data.messaging_settings
}

/**
 * @param {{ appointment_confirmation_enabled?: boolean, promotional_template_body?: string | null }} payload
 * @returns {Promise<MessagingSettings>}
 */
export async function updateMessagingSettings(payload) {
  const { data } = await api.put('/masters/messaging-settings', payload)
  return data.data.messaging_settings
}

/**
 * @param {{ offer_text: string, audience?: 'all_with_phone' | 'marketing_consent' | 'selected', customer_ids?: number[] }} payload
 * @returns {Promise<{ sent: number, failed: number, skipped: number, total: number }>}
 */
export async function sendPromotionalMessage(payload) {
  const { data } = await api.post('/masters/messaging/promotional-send', payload)
  return data.data.result
}

/**
 * @typedef {object} PromotionalAudienceCustomer
 * @property {number} id
 * @property {string} name
 * @property {string | null} phone
 * @property {boolean} marketing_consent
 * @property {string | null} customer_since
 * @property {string | null} customer_since_label
 * @property {number} member_days
 * @property {number} visit_count
 * @property {string} lifetime_spend
 * @property {string | null} last_visit_at
 * @property {string} last_visit_label
 * @property {number | null} days_since_visit
 */

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<{ items: PromotionalAudienceCustomer[], meta: import('@/types/api').PaginationMeta }>}
 */
export async function fetchPromotionalAudience(params = {}) {
  const { data } = await api.get('/masters/messaging/promotional-audience', { params })
  const meta = data.data.meta ?? {}

  return {
    items: data.data.customers ?? [],
    meta: {
      current_page: meta.current_page ?? 1,
      last_page: meta.last_page ?? 1,
      per_page: meta.per_page ?? 25,
      total: meta.total ?? 0,
    },
  }
}
